// src/hooks/useDrawRoute.js
import { useCallback, useEffect, useRef } from "react"
import { gsap } from "gsap"
import { reversePathD } from "./reversePathD"

export function useDrawRoute() {
  const containerRef = useRef(null)
  const timelineRef = useRef(null)
  const glowCloneRef = useRef(null)
  const flowCloneRef = useRef(null)
  const startPulseRef = useRef(null)
  const endPulseRef = useRef(null)

  const cleanupExtras = useCallback(() => {
    glowCloneRef.current?.remove()
    flowCloneRef.current?.remove()
    startPulseRef.current?.remove()
    endPulseRef.current?.remove()
    glowCloneRef.current = null
    flowCloneRef.current = null
    startPulseRef.current = null
    endPulseRef.current = null
  }, [])

  const play = useCallback((options = {}) => {
    const { reverse = false } = options
    const root = containerRef.current
    if (!root) return null

    const svgEl = root.querySelector("svg")
    const path = root.querySelector("#routePath")
    const startMarker = root.querySelector("#routeStart")
    const endMarker = root.querySelector("#routeEnd")
    if (!svgEl || !path) return null

    timelineRef.current?.kill()
    cleanupExtras()

    // Cache the file's authored geometry once per mount. Every subsequent
    // play() picks between the original d and a point-sampled REVERSAL of
    // it — never a dashoffset sign trick, which doesn't reliably reverse
    // a curved path.
    if (!path.dataset.origD) {
      path.dataset.origD = path.getAttribute("d")
    }

    if (reverse) {
      if (!path.dataset.revD) {
        path.dataset.revD = reversePathD(path.dataset.origD)
      }
      path.setAttribute("d", path.dataset.revD)
    } else {
      path.setAttribute("d", path.dataset.origD)
    }

    const length = path.getTotalLength()

    const glowClone = path.cloneNode(true)
    glowClone.removeAttribute("id")
    glowClone.setAttribute("class", "route-path-glow-clone")
    path.parentNode.insertBefore(glowClone, path)
    glowCloneRef.current = glowClone

    const flowClone = path.cloneNode(true)
  flowClone.removeAttribute("style")   // drop leftover dasharray/dashoffset inherited from `path`
  flowClone.removeAttribute("id")
  flowClone.setAttribute("class", "route-path-flow")
  path.parentNode.insertBefore(flowClone, path.nextSibling)
  flowCloneRef.current = flowClone

    gsap.set([path, glowClone], { strokeDasharray: length, strokeDashoffset: length })
    gsap.set([startMarker, endMarker], { opacity: 0, scale: 0.6, transformOrigin: "center" })
    gsap.set(flowClone, { opacity: 0 })
    gsap.set(glowClone, { opacity: 0.55 })

    const tl = gsap.timeline()
    timelineRef.current = tl

    tl.to([path, glowClone], { strokeDashoffset: 0, duration: 1.1, ease: "power2.inOut" })
      .to(
        [startMarker, endMarker],
        { opacity: 1, scale: 1, duration: 0.4, ease: "back.out(3)", stagger: 0.1 },
        "-=0.15"
      )
      .to(glowClone, { opacity: 0.26, duration: 0.9, yoyo: true, repeat: -1, ease: "sine.inOut" }, "<")
      .to(flowClone, { opacity: 0.9, duration: 0.4 }, "-=0.3")
      .call(() => {
        // Continuous colored pulse at each endpoint — cloned so the
        // one-shot pop-in tween above is untouched by the infinite loop.
        if (startMarker) {
          const p = startMarker.cloneNode(true)
          p.classList.add("route-marker-pulse")
          startMarker.parentNode.appendChild(p)
          startPulseRef.current = p
        }
        if (endMarker) {
          const p = endMarker.cloneNode(true)
          p.classList.add("route-marker-pulse")
          endMarker.parentNode.appendChild(p)
          endPulseRef.current = p
        }
      })

    return tl
  }, [cleanupExtras])

  const stop = useCallback(() => {
    timelineRef.current?.kill()
    timelineRef.current = null
    cleanupExtras()
  }, [cleanupExtras])

  useEffect(() => {
    return () => {
      timelineRef.current?.kill()
      cleanupExtras()
    }
  }, [cleanupExtras])

  return { containerRef, play, stop }
}